import type { AuthResponse, ErrorKey, GameSummary } from '@chess-uno/shared-protocol'

const KEY = 'chess-uno:session'

export const SERVER_URL: string =
  (import.meta.env.VITE_SERVER_URL as string | undefined)?.replace(/\/$/, '') || (typeof window === 'undefined' ? '' : window.location.origin)

export interface StoredSession {
  playerId: string
  name: string
  token: string
}

/** Key used before the rename (Uno-Chess → Chess-Uno): the session is read from it once. */
const LEGACY_KEY = 'uno-chess:session'

export function loadSession(): StoredSession | null {
  try {
    let raw = localStorage.getItem(KEY)
    if (!raw) {
      raw = localStorage.getItem(LEGACY_KEY)
      if (raw) {
        localStorage.setItem(KEY, raw)
        localStorage.removeItem(LEGACY_KEY)
      }
    }
    if (!raw) return null
    const s = JSON.parse(raw) as StoredSession
    if (!s.token || !s.playerId) return null
    return s
  } catch {
    return null
  }
}

export function clearSession(): void {
  localStorage.removeItem(KEY)
}

/** Error returned by the API: French text, plus the message key when the server sent one. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly key?: ErrorKey,
  ) {
    super(message)
  }
}

async function authRequest(path: 'register' | 'login', username: string, password: string): Promise<StoredSession> {
  const res = await fetch(`${SERVER_URL}/api/auth/${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; key?: ErrorKey }
    throw body.error ? new ApiError(body.error, body.key) : new ApiError('Connexion impossible.', 'login_failed')
  }
  const data = (await res.json()) as AuthResponse
  const session: StoredSession = { playerId: data.playerId, name: data.name, token: data.token }
  localStorage.setItem(KEY, JSON.stringify(session))
  return session
}

export function register(username: string, password: string): Promise<StoredSession> {
  return authRequest('register', username, password)
}

export function login(username: string, password: string): Promise<StoredSession> {
  return authRequest('login', username, password)
}

/** Revokes the session on the server, then forgets it locally. */
export async function logout(session: StoredSession): Promise<void> {
  await fetch(`${SERVER_URL}/api/auth/logout`, { method: 'POST', headers: { authorization: `Bearer ${session.token}` } }).catch(() => undefined)
  clearSession()
}

/** Returns null if the session is no longer recognized by the server (it is then cleared). */
export async function fetchMyGames(session: StoredSession): Promise<GameSummary[] | null> {
  const res = await fetch(`${SERVER_URL}/api/me/games`, { headers: { authorization: `Bearer ${session.token}` } })
  if (res.status === 401) {
    clearSession()
    return null
  }
  if (!res.ok) return []
  const body = (await res.json()) as { games: GameSummary[] }
  return body.games
}
